import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { FullCrudPage } from "@/components/admin/FullCrudPage";
import { SelectField, TextField } from "@/components/admin/fields";
import {
  createContact,
  deleteContact,
  getContact,
  listContacts,
  updateContact,
} from "@/services/api/contacts";
import { listCustomers } from "@/services/api/customers";
import { listHauliers } from "@/services/api/hauliers";
import { listSites } from "@/services/api/sites";
import type { Contact, ContactOwnerType } from "@/services/api/types";

interface ContactForm {
  id: string;
  owner_type: string;
  owner_id: string;
  first_name: string;
  surname: string;
  phone: string;
  email: string;
  role: string;
  parent_id: string;
}

const emptyForm: ContactForm = {
  id: "",
  owner_type: "customer",
  owner_id: "",
  first_name: "",
  surname: "",
  phone: "",
  email: "",
  role: "",
  parent_id: "",
};

const contactName = (c: Pick<Contact, "first_name" | "surname">): string =>
  [c.first_name, c.surname].filter(Boolean).join(" ") || "—";

export function ContactsPage() {
  const { t } = useTranslation();
  const [offset, setOffset] = useState(0);
  const limit = 25;

  const query = useQuery({
    queryKey: ["contacts", offset],
    queryFn: () => listContacts({ limit, offset }),
  });
  const allContacts = useQuery({
    queryKey: ["contacts", "all"],
    queryFn: () => listContacts({ limit: 500 }),
  });
  const customers = useQuery({
    queryKey: ["customers", "all"],
    queryFn: () => listCustomers({ limit: 500 }),
  });
  const hauliers = useQuery({
    queryKey: ["hauliers", "all"],
    queryFn: () => listHauliers({ limit: 500 }),
  });
  const sites = useQuery({
    queryKey: ["sites", "all"],
    queryFn: () => listSites({ limit: 500 }),
  });

  const ownerLabel = (c: Contact): string => {
    if (c.owner_type === "customer") {
      const owner = customers.data?.items.find((x) => x.id === c.owner_id);
      return owner ? `${owner.customer_no} — ${owner.name}` : `#${c.owner_id}`;
    }
    if (c.owner_type === "haulier") {
      const owner = hauliers.data?.items.find((x) => x.id === c.owner_id);
      return owner ? `${owner.haulier_code} — ${owner.name}` : `#${c.owner_id}`;
    }
    const owner = sites.data?.items.find((x) => x.id === c.owner_id);
    return owner ? `${owner.site_code} — ${owner.name}` : `#${c.owner_id}`;
  };

  const ownerOptions = (ownerType: string) => {
    if (ownerType === "customer") {
      return (customers.data?.items ?? []).map((x) => ({
        value: x.id.toString(),
        label: `${x.customer_no} — ${x.name}`,
      }));
    }
    if (ownerType === "haulier") {
      return (hauliers.data?.items ?? []).map((x) => ({
        value: x.id.toString(),
        label: `${x.haulier_code} — ${x.name}`,
      }));
    }
    return (sites.data?.items ?? []).map((x) => ({
      value: x.id.toString(),
      label: `${x.site_code} — ${x.name}`,
    }));
  };

  const ownerTypeOptions = [
    { value: "customer", label: t("nav.customers") },
    { value: "haulier", label: t("nav.hauliers") },
    { value: "site", label: t("nav.sites") },
  ];

  const columns: ColumnDef<Contact>[] = [
    {
      id: "name",
      header: t("admin.name"),
      cell: ({ row }) => contactName(row.original),
    },
    {
      id: "owner",
      header: t("admin.owner"),
      cell: ({ row }) => ownerLabel(row.original),
    },
    {
      accessorKey: "phone",
      header: t("admin.phone"),
      cell: ({ getValue }) => String(getValue() ?? "—"),
    },
    {
      accessorKey: "email",
      header: t("admin.email"),
      cell: ({ getValue }) => String(getValue() ?? "—"),
    },
    {
      accessorKey: "role",
      header: t("admin.role"),
      cell: ({ getValue }) => String(getValue() ?? "—"),
    },
  ];

  return (
    <FullCrudPage<Contact, ContactForm>
      title={t("nav.contacts")}
      entityType="contact"
      getRecord={getContact}
      columns={columns}
      data={query.data?.items ?? []}
      total={query.data?.total ?? 0}
      isLoading={query.isLoading}
      limit={limit}
      offset={offset}
      onPageChange={setOffset}
      queryKey="contacts"
      emptyForm={emptyForm}
      toForm={(r) => ({
        id: String(r.id),
        owner_type: r.owner_type,
        owner_id: String(r.owner_id),
        first_name: r.first_name ?? "",
        surname: r.surname ?? "",
        phone: r.phone ?? "",
        email: r.email ?? "",
        role: r.role ?? "",
        parent_id: r.parent_id?.toString() ?? "",
      })}
      canSubmit={(f) => Boolean(f.owner_id && (f.first_name || f.surname))}
      renderForm={(form, setForm) => {
        const parentOptions = (allContacts.data?.items ?? [])
          .filter(
            (c) =>
              c.owner_type === form.owner_type &&
              c.owner_id === Number(form.owner_id) &&
              c.id !== Number(form.id),
          )
          .map((c) => ({ value: c.id.toString(), label: contactName(c) }));
        return (
          <div className="grid grid-cols-2 gap-4">
            <SelectField
              label={t("admin.ownerType")}
              value={form.owner_type}
              onChange={(v) => setForm({ ...form, owner_type: v, owner_id: "", parent_id: "" })}
              options={ownerTypeOptions}
            />
            <SelectField
              label={t("admin.owner")}
              value={form.owner_id}
              onChange={(v) => setForm({ ...form, owner_id: v, parent_id: "" })}
              options={[{ value: "", label: "—" }, ...ownerOptions(form.owner_type)]}
            />
            <TextField
              label={t("admin.firstName")}
              value={form.first_name}
              onChange={(v) => setForm({ ...form, first_name: v })}
            />
            <TextField
              label={t("admin.surname")}
              value={form.surname}
              onChange={(v) => setForm({ ...form, surname: v })}
            />
            <TextField
              label={t("admin.phone")}
              value={form.phone}
              onChange={(v) => setForm({ ...form, phone: v })}
            />
            <TextField
              label={t("admin.email")}
              value={form.email}
              onChange={(v) => setForm({ ...form, email: v })}
            />
            <SelectField
              label={t("admin.parentContact")}
              value={form.parent_id}
              onChange={(v) => setForm({ ...form, parent_id: v })}
              options={[{ value: "", label: "—" }, ...parentOptions]}
            />
            <TextField
              label={t("admin.role")}
              value={form.role}
              onChange={(v) => setForm({ ...form, role: v })}
            />
          </div>
        );
      }}
      onSave={async (mode, form, record) => {
        const payload = {
          owner_type: form.owner_type as ContactOwnerType,
          owner_id: Number(form.owner_id),
          first_name: form.first_name || null,
          surname: form.surname || null,
          phone: form.phone || null,
          email: form.email || null,
          role: form.role || null,
          parent_id: form.parent_id ? Number(form.parent_id) : null,
        };
        return mode === "edit" && record
          ? updateContact(record.id, payload)
          : createContact(payload);
      }}
      onDelete={(record) => deleteContact(record.id)}
      recordTitle={(r) => contactName(r)}
      detailFields={(r) => [
        { label: t("admin.name"), value: contactName(r) },
        { label: t("admin.owner"), value: ownerLabel(r) },
        { label: t("admin.phone"), value: r.phone ?? "—", field: "phone" },
        { label: t("admin.email"), value: r.email ?? "—", field: "email" },
        { label: t("admin.role"), value: r.role ?? "—", field: "role" },
      ]}
    />
  );
}
